/**
 * 68.43, the basic radar picture (the owner, Oct 20: "the basic always on render was just the circular radius (keeping
 * terrain modulation)"): for each radar the side may draw (ui/radarCoverage.ts — its own, the enemy's it has seen), one
 * line on the ground where its gun can engage at the low height: the edge of its reach ∩ what its radar resolves. Built
 * once in planet space when the reach's grid is complete (a `LineSegments2` per radar, a constant width in pixels, under
 * the terrain's depth), rebuilt only when the grid is (the ground near it changed); the camera only moves. Shown with the
 * command view, faded with it.
 */
import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import type { GameState } from '../types';
import { TEAMS } from '../constants';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import type { Coverage } from '../ui/radarCoverage';
import { nodesOf, contourSegments } from './radarCoverageDraw';

/** The line's width (CSS pixels) and its lift off the drawn ground (people: over the chords between the grid's nodes). */
const WIDTH = 2, LIFT_PEOPLE = 1;

export class RadarReachView {
  private readonly group = new THREE.Group();
  private kept = new Map<Coverage, { line: LineSegments2; material: LineMaterial; heightmap: Float32Array } | null>();
  private readonly size = new THREE.Vector2();
  /** Lines built since the match began (the frame budget's readout: none while the grids stand). */
  builds = 0;

  constructor(private readonly scene: THREE.Scene) {
    this.group.name = 'radarReach';
    scene.add(this.group);
  }

  /** Each frame: the reach's grids (complete ones get a line, once), shown at `alpha` (0: hidden). */
  update(state: GameState, grids: readonly Coverage[], alpha: number, renderer: { getSize(v: THREE.Vector2): THREE.Vector2 } | null): void {
    const live = new Set(grids);
    for (const [c, v] of this.kept) if (!live.has(c)) { if (v) { this.group.remove(v.line); v.line.geometry.dispose(); v.material.dispose(); } this.kept.delete(c); }
    this.group.visible = alpha > 0.01;
    if (!this.group.visible) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    if (renderer) renderer.getSize(this.size); else this.size.set(window.innerWidth, window.innerHeight);
    for (const c of grids) {
      const planet = state.planets[c.planetIndex];
      let v = this.kept.get(c);
      if (v && v.heightmap !== planet.heightmap) { this.group.remove(v.line); v.line.geometry.dispose(); v.material.dispose(); this.kept.delete(c); v = undefined; }
      if (v === undefined) {
        if (c.done < c.cells.length) continue;   // (sampling: drawn when complete)
        const nodes = nodesOf(state, c, LIFT_PEOPLE * person);
        const seg = contourSegments(c, nodes, 0, 2);
        if (!seg.length) { this.kept.set(c, null); continue; }
        const geometry = new LineSegmentsGeometry();
        geometry.setPositions(seg);
        const colour = TEAMS[((c.team % TEAMS.length) + TEAMS.length) % TEAMS.length]?.primary ?? '#c8ccd8';
        const material = new LineMaterial({ color: new THREE.Color(colour).getHex(), linewidth: WIDTH, transparent: true, depthWrite: false, worldUnits: false });
        const line = new LineSegments2(geometry, material);
        line.position.set(nodes.xyz[0], nodes.xyz[1], nodes.xyz[2]);   // (the segments are relative to the head: float precision)
        line.frustumCulled = false;
        line.renderOrder = 2;
        this.group.add(line);
        v = { line, material, heightmap: planet.heightmap };
        this.kept.set(c, v);
        this.builds++;
      }
      if (v) { v.material.opacity = Math.min(1, alpha) * (c.own ? 0.95 : 0.8); v.material.resolution.copy(this.size); }
    }
  }

  /** Out of the command view: hidden, the lines kept. */
  hide(): void { this.group.visible = false; }

  dispose(): void {
    for (const v of this.kept.values()) if (v) { v.line.geometry.dispose(); v.material.dispose(); }
    this.kept.clear();
    this.scene.remove(this.group);
  }
}
